/**
 * @file apps/web/src/components/TemplateManagerPanel.tsx
 * @description Setup-tab UI for a project's reusable approval workflow
 * templates — list, create, edit, delete. Create/edit/delete controls are
 * only rendered when `isAdmin` is true (project lead or tenant owner/admin);
 * everyone with project access can view the list read-only. Server-side
 * enforcement lives in apps/api/src/routes/approvalTemplates.ts.
 */

import { useEffect, useState, useCallback } from 'react';
import { Plus, X, Pencil, Trash2, AlertCircle } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { approvalTemplatesApi } from '../lib/api.js';
import { RoutingBuilder, type RoutingStep, type WorkflowType, type TeamMember } from './RoutingBuilder.js';

interface TemplateStep {
  id:             string;
  step_order:     number;
  approver_id:    string;
  approver_name:  string | null;
  approver_email: string;
  parallel_group: number | null;
  is_required:    boolean;
}

interface Template {
  id:            string;
  name:          string;
  workflow_type: WorkflowType;
  steps:         TemplateStep[];
}

function stepsToRouting(steps: TemplateStep[]): RoutingStep[] {
  return steps.map((s, i) => ({
    tempId:        i,
    approverId:    s.approver_id,
    approverName:  s.approver_name ?? s.approver_email,
    approverEmail: s.approver_email,
    parallelGroup: s.parallel_group ?? undefined,
    isRequired:    s.is_required,
  }));
}

function TemplateForm({
  teamMembers,
  initial,
  onSubmit,
  onCancel,
}: {
  teamMembers: TeamMember[];
  initial?:    Template;
  onSubmit:    (body: { name: string; workflowType: WorkflowType; steps: RoutingStep[] }) => Promise<void>;
  onCancel:    () => void;
}) {
  const [name, setName]     = useState(initial?.name ?? '');
  const [wfType, setWfType] = useState<WorkflowType>(initial?.workflow_type ?? 'sequential');
  const [steps, setSteps]   = useState<RoutingStep[]>(initial ? stepsToRouting(initial.steps) : []);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr]       = useState('');

  const handleSubmit = async () => {
    if (!name.trim())       { setErr('Name is required.'); return; }
    if (steps.length === 0) { setErr('Add at least one approver.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      await onSubmit({ name: name.trim(), workflowType: wfType, steps });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-ink">
          {initial ? 'Edit Template' : 'New Template'}
        </h3>
        <button onClick={onCancel} className="btn-icon text-muted hover:text-ink">
          <X size={16} />
        </button>
      </div>

      <div>
        <label className="label mb-1.5">Template Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Standard P2P Approval"
          className="input text-sm"
        />
      </div>

      <RoutingBuilder
        teamMembers={teamMembers}
        value={steps}
        workflowType={wfType}
        onChange={(s, t) => { setSteps(s); setWfType(t); }}
        allowedTypes={['sequential', 'parallel']}
      />

      {err && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <AlertCircle size={13} /> {err}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-[rgba(8,17,31,0.06)]">
        <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button onClick={() => void handleSubmit()} disabled={submitting} className="btn-primary text-sm">
          {submitting ? 'Saving…' : initial ? 'Save Changes' : 'Create Template'}
        </button>
      </div>
    </div>
  );
}

export function TemplateManagerPanel({
  projectId,
  teamMembers,
  isAdmin,
}: {
  projectId:   string;
  teamMembers: TeamMember[];
  isAdmin:     boolean;
}) {
  const { devHeaders } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [showNew, setShowNew]     = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await approvalTemplatesApi.list(projectId, devHeaders) as Template[];
      setTemplates(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (body: { name: string; workflowType: WorkflowType; steps: RoutingStep[] }) => {
    await approvalTemplatesApi.create({
      project_id: projectId,
      name: body.name,
      workflow_type: body.workflowType,
      steps: body.steps.map((s, i) => ({
        approver_id: s.approverId,
        step_order: i + 1,
        parallel_group: s.parallelGroup,
        is_required: s.isRequired,
      })),
    }, devHeaders);
    setShowNew(false);
    await load();
  };

  const handleUpdate = async (id: string, body: { name: string; workflowType: WorkflowType; steps: RoutingStep[] }) => {
    await approvalTemplatesApi.update(id, {
      name: body.name,
      workflow_type: body.workflowType,
      steps: body.steps.map((s, i) => ({
        approver_id: s.approverId,
        step_order: i + 1,
        parallel_group: s.parallelGroup,
        is_required: s.isRequired,
      })),
    }, devHeaders);
    setEditingId(null);
    await load();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this template? This cannot be undone.')) return;
    await approvalTemplatesApi.delete(id, devHeaders);
    await load();
  };

  if (loading) {
    return <div className="card h-32 animate-pulse bg-gray/60" />;
  }

  return (
    <div className="space-y-4">
      {isAdmin && !showNew && !editingId && (
        <div className="flex justify-end">
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm">
            <Plus size={14} /> New Template
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {showNew && (
        <TemplateForm
          teamMembers={teamMembers}
          onSubmit={handleCreate}
          onCancel={() => setShowNew(false)}
        />
      )}

      {templates.length === 0 && !showNew ? (
        <div className="empty-state py-16">
          <p className="text-sm text-muted">No approval workflow templates yet.</p>
          {isAdmin && (
            <button onClick={() => setShowNew(true)} className="btn-secondary mt-4">
              <Plus size={14} /> Create First Template
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            editingId === t.id ? (
              <TemplateForm
                key={t.id}
                teamMembers={teamMembers}
                initial={t}
                onSubmit={(body) => handleUpdate(t.id, body)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div key={t.id} className="card p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-ink">{t.name}</p>
                    <p className="text-[11px] font-mono text-muted mt-0.5">
                      {t.workflow_type} · {t.steps.length} approver{t.steps.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditingId(t.id)} className="btn-icon w-7 h-7 text-muted hover:text-ink">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => void handleDelete(t.id)} className="btn-icon w-7 h-7 text-muted hover:text-danger">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {t.steps.map((s) => (
                    <span key={s.id} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-sky text-blue">
                      {s.step_order}. {s.approver_name ?? s.approver_email}
                    </span>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
